import { BescheidLoadPhase } from '../enums/bescheid-load-phase.enum';
import { LoadState } from '../enums/load-state.enum';
import { BescheidLoadComplete, BescheidLoadProgress, LoadedFile } from '../models/BescheidLoad';

const BYTES_PER_MB = 1048576;
const NO_PHASE_COMPLETED = 'no phase completed';

/**
 * Produces the progress updates of a load and records how long each phase took.
 *
 * The reason for measuring is a practical one: a support report like "the page stays white" cannot
 * be acted on, because it does not say whether the connection, the decryption or the unpacking is
 * slow — and those have entirely different causes.
 */
export class PhaseTimings {
  private readonly entries: string[] = [];
  private phase: BescheidLoadPhase | null = null;
  private startedAt = 0;
  private loadedBytes = 0;

  /** Closes the running phase and opens the given one. */
  public enter(phase: BescheidLoadPhase): BescheidLoadProgress {
    this.close();
    this.phase = phase;
    this.startedAt = performance.now();

    return { state: LoadState.Loading, phase, percent: null };
  }

  public downloading(loadedBytes: number, totalBytes?: number): BescheidLoadProgress {
    this.loadedBytes = loadedBytes;

    return {
      state: LoadState.Loading,
      phase: BescheidLoadPhase.Downloading,
      // Without Content-Length there is no honest percentage; the view stays indeterminate then.
      percent: totalBytes ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : null,
    };
  }

  /**
   * Records the transferred size without touching the running phase.
   *
   * {@link downloading} is the only other place that sets it, and a response the server delivers in
   * a single chunk produces no progress event at all. Without this the log line would claim
   * `0.0 MB` for a transfer of several megabytes — precisely the number a support report is read
   * for.
   *
   * @param {number} loadedBytes - Size of the received payload.
   */
  public downloaded(loadedBytes: number): void {
    this.loadedBytes = Math.max(this.loadedBytes, loadedBytes);
  }

  public done(files: LoadedFile[]): BescheidLoadComplete {
    this.close();

    return { state: LoadState.Done, files };
  }

  /**
   * The phase durations as a log line. Closes a still-open phase as well, so that on failure the
   * phase it failed in is part of the output.
   *
   * @return {string} For example `downloading 19.9 MB 7395 ms, decrypting 293 ms`.
   */
  public render(): string {
    this.close();

    return this.entries.join(', ') || NO_PHASE_COMPLETED;
  }

  private close(): void {
    if (!this.phase) {
      return;
    }

    const durationMs = Math.round(performance.now() - this.startedAt);
    const size =
      this.phase === BescheidLoadPhase.Downloading ? ` ${(this.loadedBytes / BYTES_PER_MB).toFixed(1)} MB` : '';
    this.entries.push(`${this.phase}${size} ${durationMs} ms`);
    this.phase = null;
  }
}
